// LLM generated - Model Claude Opus 4.5
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { STALE_TIME, CACHE_TIME } from '~/common/constants/react-query.const';
import { getBonuses } from '~/services/bonuses/bonuses.service.api';
import { bugSnagLog } from '~/utils/bugsnag';
import type { NormalizedBonus } from '~/domains/promotions/types/bonus.types';
import { normalizeBonuses } from '~/domains/promotions/utils/transformers';

/**
 * Parameters for fetching registration bonuses
 */
type UseRegistrationBonusesParams = {
  /** Whether the query should be enabled */
  enabled?: boolean;
};

/**
 * Normalized response for registration bonuses
 */
type NormalizedRegistrationBonusesResponse = {
  data: NormalizedBonus[];
  totalCount: number;
};

/**
 * React Query hook for fetching public registration bonuses
 * Uses the public /bonuses endpoint (no authentication required) with trigger=REGISTRATION filter
 *
 * @param params enabled flag for controlling query execution
 * @returns Query result with normalized registration bonuses data, loading state, and error handling
 */
export const useRegistrationBonuses = ({ enabled = true }: UseRegistrationBonusesParams = {}) =>
  useQuery<NormalizedRegistrationBonusesResponse, Error>({
    queryKey: ['bonuses', 'registration', 'public'],
    queryFn: async () => {
      const response = await getBonuses({
        trigger: 'REGISTRATION',
        page: 1,
        pageSize: 10,
      });
      // Normalize bonuses for UI display
      const normalizedData = normalizeBonuses(response.data);
      return {
        data: normalizedData,
        totalCount: response.totalCount,
      };
    },
    staleTime: STALE_TIME.DEFAULT, // 5 minutes - bonuses don't change frequently
    cacheTime: CACHE_TIME.DEFAULT, // 10 minutes - reasonable cache time
    enabled,
    onError: (error: AxiosError<{ message?: string }>) => {
      // Log error for debugging - no user notification since this is a non-critical feature
      bugSnagLog({
        errorMessage: error.message,
        context: 'useRegistrationBonuses hook',
        severity: 'warning',
        errorDetails: error,
      });
    },
  });
